import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, AlertCircle, Clock12, Database, Layers } from 'lucide-react';
import WeekCalendar from '../../components/environments/WeekCalendar';
import EnvironmentStateControl from '../../components/environments/EnvironmentStateControl';
import ScheduleEditor from "../../components/environments/ScheduleEditor";
import EnvironmentDeleteButton from "../../components/environments/EnvironmentDeleteButton";
import ClusterManagement from '../../components/environments/ClusterManagement';
import ApiService from '../../services/api';

const EnvironmentDetailPage = () => {
    const { envName } = useParams();
    const [environment, setEnvironment] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchEnvironmentDetails();
    }, [envName]);

    useEffect(() => {
        if (environment) {
            fetchEnvironmentSchedule();
        }
    }, [environment]);

    const fetchEnvironmentDetails = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await ApiService.getEnvironment(envName);
            setEnvironment(response.data.environment);
        } catch (error) {
            console.error('Error fetching environment details:', error);
            setError(`Failed to load environment: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchEnvironmentSchedule = async () => {
        try {
            const response = await ApiService.getSchedule(envName);

            setSchedule(response.data.weeklySchedule || {});
        } catch (error) {
            console.error('Error fetching environment schedule:', error);

            setSchedule({
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: []
            });
        }
    };

    const handleStateChanged = (updatedEnvironment) => {
        setEnvironment(updatedEnvironment);

        let count = 0;
        const maxPolls = 5;

        const pollInterval = setInterval(async () => {
            count++;
            try {
                const response = await ApiService.getEnvironmentState(envName);
                if (response.data && response.data.environment) {
                    setEnvironment(response.data.environment);

                    if (response.data.environment.state !== 'Transitioning') {
                        clearInterval(pollInterval);
                    }
                }
            } catch (error) {
                console.error('Error polling environment state:', error);
            }

            if (count >= maxPolls) {
                clearInterval(pollInterval);
            }
        }, 5000);
    };

    const handleScheduleSave = async (scheduleData) => {
        try {
            await ApiService.updateSchedule(envName, scheduleData);

            await fetchEnvironmentSchedule();
            return true;
        } catch (error) {
            console.error('Error saving schedule:', error);
            return false;
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-red-700">
                            {error}
                        </p>
                        <button
                            onClick={fetchEnvironmentDetails}
                            className="mt-2 px-3 py-1 text-sm text-white bg-red-500 hover:bg-red-600 rounded"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!environment) {
        return null;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 capitalize">{envName} Environment</h1>
                    <p className="text-gray-600">Manage schedule and resources for this environment</p>
                </div>
                <div className="flex space-x-4">
                    <EnvironmentStateControl
                        environmentName={envName}
                        currentState={environment.state}
                        onStateChanged={handleStateChanged}
                    />
                    <ScheduleEditor
                        environmentName={envName}
                        initialSchedule={schedule}
                        onSave={handleScheduleSave}
                    />
                    <EnvironmentDeleteButton environmentName={envName} />
                </div>
            </div>

            {/* Environment Status Details */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold mb-4">Cluster Management</h2>
                <ClusterManagement
                    environmentName={envName}
                    environmentId={environment.name}
                    onUpdate={fetchEnvironmentDetails}
                />
                <h2 className="text-lg font-semibold mb-4">Environment Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center mb-2">
                            <Database className="h-5 w-5 text-blue-500 mr-2"/>
                            <h3 className="font-medium">Database</h3>
                        </div>
                        <p className="text-sm text-gray-600">Instance ID: {environment.dbInstanceId}</p>
                        <p className="text-sm text-gray-600">State: {environment.state}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center mb-2">
                            <Layers className="h-5 w-5 text-blue-500 mr-2"/>
                            <h3 className="font-medium">AWS Account</h3>
                        </div>
                        <p className="text-sm text-gray-600">Account ID: {environment.accountId}</p>
                        <p className="text-sm text-gray-600">Region: {environment.region}</p>
                        <p className="text-sm text-gray-600 truncate">Role: {environment.roleArn}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center mb-2">
                            <Clock12 className="h-5 w-5 text-blue-500 mr-2"/>
                            <h3 className="font-medium">Last Update</h3>
                        </div>
                        <p className="text-sm text-gray-600">
                            {new Date(environment.lastUpdated).toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600">
                            {environment.lastOperation || 'No recent operations'}
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Weekly Schedule</h2>
                    <div className="flex space-x-4">
                        <div className="flex items-center">
                            <div className="w-3 h-3 bg-green-300 mr-2"></div>
                            <span className="text-sm text-gray-600">Active Hours</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-3 h-3 bg-red-300 mr-2"></div>
                            <span className="text-sm text-gray-600">Inactive Hours</span>
                        </div>
                    </div>
                </div>
                {schedule && <WeekCalendar schedule={schedule} />}
                {!schedule && (
                    <div className="text-center py-8 text-gray-500">
                        No schedule configured. Use the "Update Schedule" button to set up a schedule.
                    </div>
                )}
            </div>
        </div>
    );
};

export default EnvironmentDetailPage;